/**
 * Estate Executor Service
 *
 * Handles executor designation lifecycle for both natural persons and firms.
 * Executors are stored as estate_vault_contacts with role='executor'.
 * Extended profile metadata (executor_type, organization, representative)
 * is stored in the access_scope JSONB field until the estate_executor_profiles
 * migration table is provisioned.
 *
 * @module services/EstateExecutorService
 */

import { supabase } from '@xanectech/supabase-client/supabase-client';
import { createLogger, getErrorMessage } from '@xanectech/lib';
import type { Database } from '@xanectech/types';
import { EstateContactDocumentWorkflowService } from './EstateContactDocumentWorkflowService';

const logger = createLogger('EstateExecutorService');

// ============================================
// Row type aliases
// ============================================

type EstateContact =
  Database['public']['Tables']['estate_vault_contacts']['Row'];
type EstateContactInsert =
  Database['public']['Tables']['estate_vault_contacts']['Insert'];

// ============================================
// Executor domain types
// ============================================

export type ExecutorType = 'executor_person' | 'executor_firm';

export interface ExecutorProfile {
  executor_type: ExecutorType;
  organization_name?: string | null;
  registration_number?: string | null;
  jurisdiction_code?: string | null;
  representative_name?: string | null;
  representative_email?: string | null;
  representative_phone?: string | null;
}

/** Full executor record combining estate_vault_contacts row + profile metadata */
export interface ExecutorRecord extends EstateContact {
  profile: ExecutorProfile;
}

export interface CreateExecutorPayload {
  contact_name: string;
  contact_email: string;
  relationship: string;
  relationship_details?: string | null;
  profile: ExecutorProfile;
  /** ISO 3166-1 alpha-2 country code for document requirement evaluation. */
  country_code?: string | null;
}

export interface UpdateExecutorPayload {
  contact_name?: string;
  contact_email?: string;
  relationship?: string;
  relationship_details?: string | null;
  profile?: Partial<ExecutorProfile>;
  /** ISO 3166-1 alpha-2 country code for document requirement evaluation. */
  country_code?: string | null;
}

// ============================================
// Helpers
// ============================================

function parseProfile(row: EstateContact): ExecutorProfile {
  const scope = row.access_scope as Record<string, unknown> | null;
  return {
    executor_type:
      (scope?.executor_type as ExecutorType | undefined) ?? 'executor_person',
    organization_name: (scope?.organization_name as string | null) ?? null,
    registration_number:
      (scope?.registration_number as string | null) ?? null,
    jurisdiction_code: (scope?.jurisdiction_code as string | null) ?? null,
    representative_name:
      (scope?.representative_name as string | null) ?? null,
    representative_email:
      (scope?.representative_email as string | null) ?? null,
    representative_phone:
      (scope?.representative_phone as string | null) ?? null,
  };
}

function toExecutorRecord(row: EstateContact): ExecutorRecord {
  return { ...row, profile: parseProfile(row) };
}

// ============================================
// Service
// ============================================

export class EstateExecutorService {
  /**
   * List all active executors for the given user.
   */
  static async listExecutors(userId: string): Promise<ExecutorRecord[]> {
    const { data, error } = await supabase
      .from('estate_vault_contacts')
      .select('*')
      .eq('user_id', userId)
      .eq('role', 'executor')
      .is('revoked_at', null)
      .order('priority_order', { ascending: true });

    if (error) {
      logger.error('Failed to list executors', {
        context: 'EstateExecutorService',
        error: getErrorMessage(error),
      });
      throw new Error('EXECUTOR_FETCH_FAILED');
    }

    return (data ?? []).map(toExecutorRecord);
  }

  /**
   * Create a new executor designation.
   */
  static async createExecutor(
    userId: string,
    payload: CreateExecutorPayload,
  ): Promise<ExecutorRecord> {
    const countryCode = payload.country_code ?? null;
    const scopeData = countryCode
      ? { ...payload.profile, country_code: countryCode }
      : payload.profile;

    const insert: EstateContactInsert = {
      user_id: userId,
      contact_name: payload.contact_name,
      contact_email: payload.contact_email,
      relationship: payload.relationship,
      relationship_details: payload.relationship_details ?? null,
      role: 'executor',
      status: 'pending',
      access_scope: scopeData as unknown as Database['public']['Tables']['estate_vault_contacts']['Insert']['access_scope'],
    };

    const { data, error } = await supabase
      .from('estate_vault_contacts')
      .insert(insert)
      .select()
      .single();

    if (error || !data) {
      logger.error('Failed to create executor', {
        context: 'EstateExecutorService',
        error: getErrorMessage(error),
      });
      throw new Error('EXECUTOR_CREATE_FAILED');
    }

    // Trigger non-blocking document workflow evaluation
    const resolvedRole =
      payload.profile.executor_type === 'executor_firm'
        ? 'executor_firm'
        : 'executor_person';
    void EstateContactDocumentWorkflowService.evaluateAndPersist(
      userId,
      data.id,
      resolvedRole,
      countryCode,
    ).catch((err) => {
      logger.warn('Document workflow evaluation failed after executor create', {
        context: 'EstateExecutorService',
        error: getErrorMessage(err),
      });
    });

    return toExecutorRecord(data);
  }

  /**
   * Update an executor's contact information and/or profile metadata.
   */
  static async updateExecutor(
    userId: string,
    contactId: string,
    payload: UpdateExecutorPayload,
  ): Promise<ExecutorRecord> {
    // First fetch existing record to merge profile
    const { data: existing, error: fetchError } = await supabase
      .from('estate_vault_contacts')
      .select('*')
      .eq('id', contactId)
      .eq('user_id', userId)
      .eq('role', 'executor')
      .single();

    if (fetchError || !existing) {
      throw new Error('EXECUTOR_NOT_FOUND');
    }

    const currentProfile = parseProfile(existing);
    const mergedProfile = payload.profile
      ? { ...currentProfile, ...payload.profile }
      : currentProfile;

    // Merge country_code into the scope if provided
    const existingScope =
      (existing.access_scope as Record<string, unknown> | null) ?? {};
    const resolvedCountryCode =
      payload.country_code !== undefined
        ? payload.country_code
        : ((existingScope.country_code as string | null) ?? null);

    const mergedScope =
      resolvedCountryCode !== null
        ? { ...mergedProfile, country_code: resolvedCountryCode }
        : mergedProfile;

    const update: Partial<EstateContactInsert> = {
      contact_name: payload.contact_name ?? existing.contact_name,
      contact_email: payload.contact_email ?? existing.contact_email,
      relationship: payload.relationship ?? existing.relationship,
      relationship_details:
        payload.relationship_details !== undefined
          ? payload.relationship_details
          : existing.relationship_details,
      access_scope: mergedScope as unknown as Database['public']['Tables']['estate_vault_contacts']['Update']['access_scope'],
    };

    const { data, error } = await supabase
      .from('estate_vault_contacts')
      .update(update)
      .eq('id', contactId)
      .eq('user_id', userId)
      .select()
      .single();

    if (error || !data) {
      logger.error('Failed to update executor', {
        context: 'EstateExecutorService',
        error: getErrorMessage(error),
      });
      throw new Error('EXECUTOR_UPDATE_FAILED');
    }

    // Trigger non-blocking document workflow evaluation
    const resolvedRole =
      mergedProfile.executor_type === 'executor_firm'
        ? 'executor_firm'
        : 'executor_person';
    void EstateContactDocumentWorkflowService.evaluateAndPersist(
      userId,
      contactId,
      resolvedRole,
      resolvedCountryCode,
    ).catch((err) => {
      logger.warn('Document workflow evaluation failed after executor update', {
        context: 'EstateExecutorService',
        error: getErrorMessage(err),
      });
    });

    return toExecutorRecord(data);
  }

  /**
   * Revoke an executor designation (sets revoked_at and revoked_reason).
   */
  static async revokeExecutor(
    userId: string,
    contactId: string,
    reason: string,
  ): Promise<void> {
    const { error } = await supabase
      .from('estate_vault_contacts')
      .update({
        revoked_at: new Date().toISOString(),
        revoked_reason: reason,
        status: 'revoked',
      })
      .eq('id', contactId)
      .eq('user_id', userId)
      .eq('role', 'executor');

    if (error) {
      logger.error('Failed to revoke executor', {
        context: 'EstateExecutorService',
        error: getErrorMessage(error),
      });
      throw new Error('EXECUTOR_REVOKE_FAILED');
    }
  }
}

export type { EstateContact };
